import React, { useCallback, useEffect, useRef, useState } from 'react'
import { getWebSocket, getWsBaseUrl, isDebugEnabled } from '@/config/config'
import { logger } from '@/utils/logger'
import {
  WebSocketContext,
  type WebSocketContextValue,
  type WebSocketMessage,
  type WebSocketStatus,
  clearStoredConnectionId,
  storeConnectionId,
} from './websocket-context'

interface WebSocketProviderProps {
  children: React.ReactNode
  /** Authentication token to include in WebSocket URL */
  token?: string | null
  /** Optional custom WebSocket URL (overrides config) */
  url?: string
  /** Whether to auto-connect on mount */
  autoConnect?: boolean
}

type MessageHandler<T = unknown> = (data: WebSocketMessage<T>) => void
type Subscribers = Map<string, Set<MessageHandler>>

export function WebSocketProvider({
  children,
  token,
  url,
  autoConnect = true,
}: WebSocketProviderProps) {
  const [status, setStatus] = useState<WebSocketStatus>('disconnected')
  const [connectionId, setConnectionId] = useState<string | null>(null)
  const [reconnectAttempts, setReconnectAttempts] = useState(0)
  const [lastError, setLastError] = useState<Error | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const subscribersRef = useRef<Subscribers>(new Map())
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const shouldReconnectRef = useRef(true)
  const isConnectingRef = useRef(false)
  const previousTokenRef = useRef(token)

  const wsConfig = getWebSocket()

  /**
   * Build WebSocket URL
   */
  const buildWsUrl = useCallback((): string => {
    const base = url || getWsBaseUrl()

    if (!token) return base

    try {
      const u = new URL(base)

      return u.toString()
    } catch {
      return base
    }
  }, [url, token])

  /**
   * Calculate reconnect delay with exponential backoff
   */
  const getReconnectDelay = useCallback(
    (attempt: number): number => {
      const { reconnectBaseDelay, reconnectMaxDelay } = wsConfig
      // Exponential backoff: baseDelay * 2^attempt with jitter
      const exponentialDelay = reconnectBaseDelay * Math.pow(2, attempt)
      const jitter = Math.random() * 1000 // Add up to 1 second of jitter

      return Math.min(exponentialDelay + jitter, reconnectMaxDelay)
    },
    [wsConfig]
  )

  /**
   * Notify subscribers of a message
   */
  const notifySubscribers = useCallback((message: WebSocketMessage) => {
    const { event } = message

    // Notify event-specific subscribers
    const eventSubscribers = subscribersRef.current.get(event)

    if (eventSubscribers) {
      eventSubscribers.forEach((handler) => {
        try {
          handler(message)
        } catch (error) {
          logger.error(`Error in WebSocket message handler for event "${event}":`, error)
        }
      })
    }

    // Notify wildcard subscribers (subscribe to all events)
    const wildcardSubscribers = subscribersRef.current.get('*')

    if (wildcardSubscribers) {
      wildcardSubscribers.forEach((handler) => {
        try {
          handler(message)
        } catch (error) {
          logger.error('Error in WebSocket wildcard message handler:', error)
        }
      })
    }
  }, [])

  /**
   * Connect to WebSocket server
   */
  const connect = useCallback(() => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    isConnectingRef.current = true
    setStatus('connecting')
    setLastError(null)

    const wsUrl = buildWsUrl()

    if (isDebugEnabled()) {
      logger.debug('WebSocket connecting to:', wsUrl.replace(/token=[^&]+/, 'token=***'))
    }

    try {
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        isConnectingRef.current = false
        setStatus('connected')
        setReconnectAttempts(0)
        logger.info('WebSocket connected')
      }

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)

          if (isDebugEnabled()) {
            logger.debug('WebSocket message received:', message)
          }

          if (message.connectionId) {
            setConnectionId(message.connectionId)
            storeConnectionId(message.connectionId)
            logger.info('WebSocket connectionId received:', message.connectionId)
          }

          // Notify all subscribers
          notifySubscribers(message)
        } catch (error) {
          logger.error('Failed to parse WebSocket message:', error)
        }
      }

      ws.onerror = (event) => {
        isConnectingRef.current = false
        const error = new Error('WebSocket error occurred')

        setLastError(error)
        logger.error('WebSocket error:', event)
      }

      ws.onclose = (event) => {
        isConnectingRef.current = false
        wsRef.current = null
        setConnectionId(null)
        clearStoredConnectionId()

        if (isDebugEnabled()) {
          logger.debug('WebSocket closed:', { code: event.code, reason: event.reason })
        }

        // Check if we should attempt to reconnect
        if (shouldReconnectRef.current && reconnectAttempts < wsConfig.reconnectAttempts) {
          setStatus('reconnecting')
          const delay = getReconnectDelay(reconnectAttempts)

          logger.info(
            `WebSocket disconnected. Reconnecting in ${Math.round(delay / 1000)}s (attempt ${reconnectAttempts + 1}/${wsConfig.reconnectAttempts})`
          )

          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts((prev) => prev + 1)
            connect()
          }, delay)
        } else {
          setStatus('disconnected')
          if (reconnectAttempts >= wsConfig.reconnectAttempts) {
            logger.warn('WebSocket max reconnection attempts reached')
          }
        }
      }

      wsRef.current = ws
    } catch (error) {
      isConnectingRef.current = false
      setStatus('disconnected')
      setLastError(error instanceof Error ? error : new Error('Failed to create WebSocket'))
      logger.error('Failed to create WebSocket:', error)
    }
  }, [buildWsUrl, notifySubscribers, reconnectAttempts, wsConfig, getReconnectDelay])

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnected')
      wsRef.current = null
    }

    setStatus('disconnected')
    setConnectionId(null)
    clearStoredConnectionId()
    setReconnectAttempts(0)
    logger.info('WebSocket disconnected by client')
  }, [])

  /**
   * Manually trigger reconnection
   */
  const reconnect = useCallback(() => {
    disconnect()
    shouldReconnectRef.current = true
    setReconnectAttempts(0)

    // Small delay to ensure clean disconnect
    setTimeout(() => {
      connect()
    }, 100)
  }, [connect, disconnect])

  /**
   * Send a message through the WebSocket
   */
  const sendMessage = useCallback(<T,>(message: T) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      logger.warn('Cannot send message: WebSocket is not connected')
      return
    }

    try {
      const payload = typeof message === 'string' ? message : JSON.stringify(message)

      wsRef.current.send(payload)

      if (isDebugEnabled()) {
        logger.debug('WebSocket message sent:', message)
      }
    } catch (error) {
      logger.error('Failed to send WebSocket message:', error)
    }
  }, [])

  /**
   * Subscribe to messages of a specific event type
   * Use '*' to subscribe to all events
   */
  const subscribe = useCallback(
    <T,>(eventType: string, handler: MessageHandler<T>): (() => void) => {
      if (!subscribersRef.current.has(eventType)) {
        subscribersRef.current.set(eventType, new Set())
      }

      const handlers = subscribersRef.current.get(eventType)!

      handlers.add(handler as MessageHandler)

      // Return unsubscribe function
      return () => {
        handlers.delete(handler as MessageHandler)
        if (handlers.size === 0) {
          subscribersRef.current.delete(eventType)
        }
      }
    },
    []
  )

  // Auto-connect on mount if enabled (wait for token if provided but null)
  useEffect(() => {
    if (autoConnect) {
      shouldReconnectRef.current = true

      // If token prop is provided but not yet resolved, wait for it
      if (token === null) {
        return
      }

      connect()
    }

    // Cleanup on unmount
    return () => {
      shouldReconnectRef.current = false
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }

      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reconnect when token becomes (un)available
  useEffect(() => {
    if (!autoConnect) {
      previousTokenRef.current = token
      return
    }

    const previousToken = previousTokenRef.current

    if (previousToken === token) {
      return
    }

    previousTokenRef.current = token

    if (token === null) {
      disconnect()
      return
    }

    // No existing socket yet (e.g. waited for token); just connect
    if (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED) {
      connect()
      return
    }

    reconnect()
  }, [token, autoConnect, reconnect, disconnect, connect])

  const value: WebSocketContextValue = {
    status,
    connectionId,
    isConnected: status === 'connected',
    reconnectAttempts,
    sendMessage,
    reconnect,
    disconnect,
    subscribe,
    lastError,
  }

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>
}
